import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  RenewalIchraEmployeeRequest,
  getAllEligibleEmployees,
  getRenewalClasses,
  getRenewalPlanPeriod,
  saveIchraEmployeesWithRenewalClasses,
} from 'core'

import { Alert, Button, Card, Col, Divider, Icon, Notification, Row, Skeleton, Tooltip } from 'common/components'
import { formatYear } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import EligibleEmployeesIchraRenewalTable from './EligibleEmployeesIchraRenewalTable'

const AssignEmployeeClasses = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const { data: renewalClasses } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getRenewalClasses(),
  })
  const { data: eligibleEmployees } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getAllEligibleEmployees(),
  })

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const { sendCommand: sendIchraEmployeesWithRenewalClasses, isLoading: isRenewalClassesSubmitLoading } =
    useCommandRequestV2((values: RenewalIchraEmployeeRequest[]) => saveIchraEmployeesWithRenewalClasses(values))

  const [submitList, setSubmitList] = useState<RenewalIchraEmployeeRequest[]>([])

  const renewalYear = Number(formatYear(renewalPlanPeriod?.startDate))

  const unAssignedEmployees = submitList?.filter((x) => Number(x.classId) === -1)
  const unAssignedEmployeesCount = unAssignedEmployees.length

  useEffect(() => {
    if (eligibleEmployees) {
      const temp: RenewalIchraEmployeeRequest[] = []
      eligibleEmployees.forEach((employee) => {
        temp.push({ id: employee.employeeId, classId: employee.renewalClass?.id || (employee.ineligible ? null : -1) })
      })
      setSubmitList(temp)
    }
  }, [eligibleEmployees])

  const handleSubmit = async () => {
    const result = await sendIchraEmployeesWithRenewalClasses(submitList)
    if (result.completed) {
      Notification.success('Employees have been assigned to classes successfully!')
      history.push(ROUTE_PATHS.RENEWAL_TASKS)
    }
  }

  const submitButton = (
    <Button
      type="primary"
      disabled={unAssignedEmployeesCount > 0}
      loading={isRenewalClassesSubmitLoading}
      onClick={handleSubmit}
    >
      Submit
    </Button>
  )

  return (
    <Card title="Assign employees to classes">
      <Skeleton loading={!renewalClasses || !eligibleEmployees}>
        <Row>
          <Col sm={24} lg={17}>
            <h5 className="mb-16">Confirm your employee benefit class assignments</h5>
            <p className="color-gray-8 mb-16">
              You need to ensure that all of your benefit-eligible employees have an ICHRA class assignment for{' '}
              {renewalYear}.
            </p>
            <p className="color-gray-8 mb-16">
              Employees will default to their current class assignment for {renewalYear} unless that class is no longer
              being offered. In that case, the employee is marked as “Unassigned” and will need to be assigned to an
              eligible {renewalYear} class.
            </p>
            <p className="color-gray-8 mb-16">
              If an employee is no longer eligible for the benefit, they can be terminated as a user from your Manage
              People page or marked as an “Ineligible” user in the {renewalYear} Class assignment column.
            </p>
          </Col>
        </Row>
        {unAssignedEmployeesCount > 0 && (
          <Alert
            showIcon
            icon={<Icon fontAwesome type="fas fa-exclamation-triangle" className="color-yellow-7 mr-8" />}
            type="warning"
            className="mb-16"
            message={
              <>
                <div className="mb-4">
                  You have{' '}
                  <b>
                    {unAssignedEmployeesCount} {unAssignedEmployeesCount > 1 ? 'employees' : 'employee'}
                  </b>{' '}
                  who {unAssignedEmployeesCount > 1 ? 'are' : 'is'} currently not assigned to a {renewalYear} class and
                  are marked as <b>“Unassigned”</b>.{' '}
                </div>
                <div>You must assign them to a {renewalYear} class or mark them as “Ineligible” to proceed.</div>
              </>
            }
          />
        )}
        {renewalClasses && eligibleEmployees && (
          <Card title="Eligible employees">
            <EligibleEmployeesIchraRenewalTable
              renewalClasses={renewalClasses}
              employees={eligibleEmployees}
              renewalYear={renewalYear}
              translate={translate}
              submitList={submitList}
              onSelectedItemsChanged={(items) => setSubmitList(items)}
            />
          </Card>
        )}
        <Divider className="mb-16 mt-32 -mx-24 w-auto" />
        <div className="flex flex__end">
          {unAssignedEmployeesCount > 0 ? (
            <Tooltip
              title="Unassigned employees!"
              overlayInnerStyle={{ background: 'transparent', padding: 0 }}
              overlay={
                <Card title="Unassigned employees!">
                  You still have {unAssignedEmployeesCount} {unAssignedEmployeesCount > 1 ? 'employees' : 'employee'}{' '}
                  not assigned to a {renewalYear} class!
                </Card>
              }
            >
              {submitButton}
            </Tooltip>
          ) : (
            submitButton
          )}
        </div>
      </Skeleton>
    </Card>
  )
}

export default AssignEmployeeClasses
